/* eslint-disable react/jsx-props-no-spreading */
import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteValue,
  Box,
  CircularProgress,
  TextFieldProps,
  Typography,
  colors,
  TextField,
} from '@mui/material';

import { isPaylinkCustomerInfo, sanitizeNumericString } from '../../util/helpers';
import { phone } from '../../util/formatters';
import { PaylinkCustomerInfoResponse } from '../../store/modules/paylink/types';
import { useDebounce } from '../../hooks/useDebounce';
import { useLazySearchPaylinkCustomersQuery } from '../../store/modules/merchant/api';
import { useMerchant } from '../../contexts/MerchantContext';
import { SearchPaylinkCustomersQueryFilters } from '../../store/modules/merchant/types';

interface Props<
  Multiple extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends Omit<
  AutocompleteProps<PaylinkCustomerInfoResponse, Multiple, false, FreeSolo>,
  'renderInput' | 'options'
> {
  freeSolo?: FreeSolo;
  multiple?: Multiple;
  inputProps?: TextFieldProps;
  /**
   * If no customer is selected from the dropdown but there's a exact match as a result of the search,
   * select it automatically in the background.
   * @default false
   */
  autoSelectCustomer?: boolean;
  /**
   * Extra query params to pass to the searchPaylinkCustomers query.
   * @default {}
   */
  extraQueryParams?: SearchPaylinkCustomersQueryFilters;
}

export const getOptionLabel = (option: PaylinkCustomerInfoResponse | string) => {
  if (isPaylinkCustomerInfo(option)) {
    if (option.firstName && option.lastName) {
      return `${option.firstName} ${option.lastName}`;
    }

    return option.phone ?? option.email ?? '';
  }

  return option;
};

export const getMatchingCustomer = (
  searchString: string,
  customerResults: PaylinkCustomerInfoResponse[],
): PaylinkCustomerInfoResponse | null => {
  if (searchString.length < 3) {
    return null;
  }

  const matchingCustomers = customerResults.filter((customer) => {
    let hasMatch = false;
    if (customer.phone) {
      hasMatch = sanitizeNumericString(customer.phone) === sanitizeNumericString(searchString);
    }

    if (!hasMatch && customer.email) {
      hasMatch = customer.email.toLowerCase() === searchString.trim().toLowerCase();
    }

    return hasMatch;
  });

  if (matchingCustomers.length === 1) {
    return matchingCustomers[0];
  }

  return null;
};

const CustomerAutoCompleteInner = <
  Multiple extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(
  {
    inputProps,
    autoSelectCustomer,
    extraQueryParams,
    ...autoCompleteProps
  }: Props<Multiple, FreeSolo>,
  ref: React.Ref<HTMLDivElement>,
) => {
  const { merchantId } = useMerchant();
  const [searchPaylinkCustomers, { isFetching: isLoadingCustomers }] =
    useLazySearchPaylinkCustomersQuery();
  const [customerResults, setCustomerResults] = useState<PaylinkCustomerInfoResponse[]>([]);
  const isSearchOpen = useRef(false);

  const { value: autoCompleteValue, onChange: autoCompleteOnChange } = autoCompleteProps;
  const autoSelectMatchingCustomer = useCallback(
    (searchString: unknown, customers: PaylinkCustomerInfoResponse[]) => {
      const hasSelectedCustomer = !!autoCompleteValue;
      if (hasSelectedCustomer || typeof searchString !== 'string') {
        return;
      }

      const matchingCustomer = getMatchingCustomer(searchString, customers);
      if (matchingCustomer) {
        autoCompleteOnChange?.(
          {
            target: { value: searchString },
          } as unknown as React.ChangeEvent<HTMLInputElement>,
          matchingCustomer as AutocompleteValue<
            PaylinkCustomerInfoResponse,
            Multiple,
            false,
            FreeSolo
          >,
          'selectOption',
        );
      }
    },
    [autoCompleteValue, autoCompleteOnChange],
  );

  const debouncedSearchCustomers = useDebounce(async (searchQuery: string) => {
    const { data: results } = await searchPaylinkCustomers({
      merchantId,
      searchQuery,
      pageSize: 10,
      ...extraQueryParams,
    }).unwrap();
    setCustomerResults(results);
    if (autoSelectCustomer && !isSearchOpen.current) {
      autoSelectMatchingCustomer(searchQuery, results);
    }
  }, 500);

  const options = useMemo(() => {
    const isMultiple = autoCompleteProps.multiple;
    if (!isMultiple) {
      return customerResults;
    }

    // Adding selected customers to the bottom of the list, so they are always visible and we
    // suppress the "no options match" warning.
    const selectedCustomers = autoCompleteProps.value as PaylinkCustomerInfoResponse[];
    const selectedCustomerIds = selectedCustomers?.map((customer) => customer.payLinkCustomerId);
    // Removing duplicates from the selected customers.
    const filteredCustomers = customerResults.filter(
      (customer) => !selectedCustomerIds?.includes(customer.payLinkCustomerId),
    );

    return [...filteredCustomers, ...selectedCustomers];
  }, [customerResults, autoCompleteProps.value, autoCompleteProps.multiple]);

  return (
    <Autocomplete
      ref={ref}
      componentsProps={{
        paper: {
          variant: 'outlined',
          sx: {
            border: `1px solid ${colors.grey[400]}`,

            fontSize: 16,
            maxHeight: '180px',
            overflow: 'auto',
          },
        },
      }}
      options={options}
      getOptionLabel={getOptionLabel}
      noOptionsText="No matches"
      loading={isLoadingCustomers}
      loadingText="Loading customers..."
      filterOptions={(opts) => opts}
      isOptionEqualToValue={(option, value) => option.payLinkCustomerId === value.payLinkCustomerId}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.payLinkCustomerId}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '2fr 4fr 3fr',
                width: '100%',
                gap: 2,
                '& p': {
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                },
              }}
            >
              <Typography>{option.phone ? phone(option.phone) : ''}</Typography>
              <Typography>{option.email}</Typography>
              <Typography>{`${option.firstName} ${option.lastName}`}</Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            variant="outlined"
            {...inputProps}
            onChange={(e) => {
              const value = e.target.value as string;
              if (value.length < 3) {
                setCustomerResults([]);
              } else {
                debouncedSearchCustomers(value);
              }
              inputProps?.onChange?.(e);
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  {isLoadingCustomers && (
                    <CircularProgress size={20} sx={{ color: colors.grey[700] }} />
                  )}
                </>
              ),
            }}
          />
        );
      }}
      {...autoCompleteProps}
      onChange={(event, value, ...rest) => {
        autoCompleteProps.onChange?.(event, value, ...rest);
        if (!value) {
          setCustomerResults([]);
        }
      }}
      onBlur={(e) => {
        autoCompleteProps.onBlur?.(e);
        if (autoSelectCustomer) autoSelectMatchingCustomer(inputProps?.value, customerResults);
      }}
      onOpen={() => {
        isSearchOpen.current = true;
      }}
      onClose={() => {
        isSearchOpen.current = false;
      }}
    />
  );
};

export const CustomerAutoComplete = forwardRef(CustomerAutoCompleteInner) as <
  Multiple extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = false,
>(
  props: Props<Multiple, FreeSolo> & { ref?: React.Ref<HTMLDivElement> },
) => ReturnType<typeof CustomerAutoCompleteInner>;
